import { Editor } from "tinymce";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { SSMLTag, underlineStyleTags } from "shared/constants";
import { titleCase } from "shared/utils/tts-custom-editor";

export const editorKeyDownHandler = (
  event: KeyboardEvent,
  editor: Editor,
  updateActiveSSMLTags: (tags: string[]) => void
): void => {
  // If user selects something and presses escape, deselect the selection
  if (event.key === "Escape") {
    editor.selection.collapse(false);
    return;
  }

  // Select opening and closing tag and overwrite them
  // this works only if user selects the whole tag
  const selectedContentText = editor.selection.getContent({
    format: "text",
  });

  // Regex to match opening and closing tags
  const regex = /^<[^>]+>.*<\/[^>]+>$/;
  const regexSelfClosingTag = /^<[^>]+\/>$/;

  // On matching, replace the selected node from the parent
  if (
    regex.test(selectedContentText) ||
    regexSelfClosingTag.test(selectedContentText)
  ) {
    // If metaKey (Command on macOS) or ctrlKey (Control on Windows) is pressed,
    if (event.metaKey || event.ctrlKey) {
      return;
    }

    // This backspace refers to when tags are selected and user presses backspace
    if (event.key === "Backspace") {
      event.preventDefault(); // prevent the default behavior

      const startNode = editor.selection.getRng().startContainer;
      editor.selection.setContent("");
      editor.selection.setCursorLocation(startNode, 0);

      if (editor.getContent({ format: "text" }).trim() === "") {
        // If editor is empty
        editor.setContent("<p><br></p>");
        const { firstChild } = editor.getBody();
        if (firstChild) {
          editor.selection.setCursorLocation(firstChild, 0);
        }
      }
      return;
    }

    // If keystroke is not an alphanumeric character, as this might be capslock or shift key or any other key
    const keystroke = event.key;
    if (!/^[a-z0-9]$/i.test(keystroke)) {
      return;
    }

    const selectedNode = editor.selection.getNode();
    const parentSsmlTag = selectedNode.parentNode;

    if (parentSsmlTag) {
      const newText = document.createTextNode(keystroke);

      // Replace the selected node with the new text node
      parentSsmlTag.replaceChild(newText, selectedNode);

      // Move the cursor to the end of the inserted keystroke
      editor.selection.setCursorLocation(newText, 1);
    }
  }

  // Click event handler to check for data-ssml attribute
  editor.on("click", (e) => {
    const element = e.target as HTMLElement;

    // Collect all SSML tags in the hierarchy
    const tags: string[] = [];
    let currentElement: HTMLElement | null = element;

    while (currentElement) {
      const ssmlData = currentElement.getAttribute("data-ssml");
      if (ssmlData) {
        tags.push(ssmlData);
      }
      currentElement = currentElement.parentElement;
    }
    if (tags.length > 0) {
      updateActiveSSMLTags(tags);
    } else {
      updateActiveSSMLTags([]);
    }
  });
};

export const getSSMLTagValue = (
  editorRef: React.RefObject<EditorRef | null>,
  tagType: SSMLTag,
  attribute: string
): string | null => {
  const editor = editorRef.current?.editor;
  if (!editor) return null;

  const selection = editor.selection;
  const selectedNode = selection.getNode();
  const ssmlElement = selectedNode.closest(`[data-ssml*="${tagType}"]`);

  return ssmlElement ? ssmlElement.getAttribute(attribute) : null;
};

export const updateSSMLTagAttributes = (
  editorRef: React.RefObject<EditorRef | null>,
  tagType: SSMLTag,
  attributes: Record<string, string>
): void => {
  const editor = editorRef.current?.editor;
  if (!editor) return;

  const selection = editor.selection;
  const selectedNode = selection.getNode();
  const ssmlElement = selectedNode.closest(`[data-ssml*="${tagType}"]`);

  if (!ssmlElement) return;

  // Convert NamedNodeMap to array and create attributes object with proper typing
  const existingAttributes: Record<string, string> = Array.from(
    ssmlElement.attributes
  ).reduce(
    (acc, attr) => ({
      ...acc,
      [attr.name]: attr.value,
    }),
    {} as Record<string, string>
  );

  // Get the data-ssml attribute and parse it
  const dataSSML = existingAttributes["data-ssml"];
  if (!dataSSML) return;

  // Split the data-ssml value to get the tag ID
  const [tagWithId] = dataSSML.split("=");

  // Combine the new value with the tag ID
  const newDataSSML = `${tagWithId}=${attributes.level}`;

  // Preserve the existing style attribute
  const existingStyle = existingAttributes["style"] || "";
  const tempDiv = editor.dom.create("div");
  tempDiv.innerHTML = ssmlElement.innerHTML;
  const newElement = editor.dom.create(
    "span",
    {
      ...existingAttributes,
      style: existingStyle,
      "data-ssml": newDataSSML,
    },
    tempDiv.innerHTML
  );
  const bookmark = editor.selection.getBookmark();
  editor.dom.replace(newElement, ssmlElement);
  editor.selection.moveToBookmark(bookmark);

  editor.fire("change");
};

export const getCurrentSSMLValue = (
  editorRef: React.RefObject<EditorRef | null>,
  tagType: SSMLTag,
  targetDepth: number = 0
): string | null => {
  if (!editorRef.current?.editor) return null;
  const editor = editorRef.current.editor;

  // Validate selection exists
  if (!editor.selection) return null;

  const rng = editor.selection.getRng();
  let currentElement: Node | null = rng.startContainer;

  if (currentElement.nodeType === Node.TEXT_NODE) {
    currentElement = currentElement.parentElement;
  }

  const matchingElements: HTMLElement[] = [];

  while (currentElement && currentElement !== editor.getBody()) {
    if (currentElement.nodeType === Node.ELEMENT_NODE) {
      const el = currentElement as HTMLElement;
      const dataSSML = el.getAttribute("data-ssml");

      if (dataSSML && dataSSML.includes(`${tagType}-`)) {
        matchingElements.push(el);
      }
    }
    currentElement = currentElement.parentElement;
  }

  if (matchingElements.length > targetDepth) {
    const targetElement = matchingElements[targetDepth];
    const dataSSML = targetElement.getAttribute("data-ssml");
    if (!dataSSML) return null;

    const parts: string[] = [];
    let currentPart = "";
    let inParenthesis = false;

    for (let i = 0; i < dataSSML.length; i++) {
      const char = dataSSML[i];
      if (char === "(") inParenthesis = true;
      if (char === ")") inParenthesis = false;

      if (char === "," && !inParenthesis) {
        if (currentPart.trim()) parts.push(currentPart.trim());
        currentPart = "";
      } else {
        currentPart += char;
      }
    }
    if (currentPart.trim()) parts.push(currentPart.trim());

    // Find and return the value for our specific tag type
    for (const part of parts) {
      if (part.startsWith(`${tagType}-`)) {
        const eqIndex = part.indexOf("=");
        if (eqIndex !== -1) {
          const value = part.substring(eqIndex + 1);
          if (
            tagType === SSMLTag.Style ||
            tagType === SSMLTag.Voice ||
            tagType === SSMLTag.Language
          ) {
            const partIndex = parts.indexOf(part);
            const remaining = parts.slice(partIndex + 1);
            const hasMoreTagParts = remaining.some((p) => p.includes("="));
            if (!hasMoreTagParts && remaining.length > 0) {
              return `${value}, ${remaining.join(", ")}`;
            }
          }

          return value;
        }
      }
    }
  }

  return null;
};

export const updateSSMLTag = (
  editorRef: React.RefObject<EditorRef | null>,
  tagType: SSMLTag,
  newValue: string,
  tagColor: string,
  uid: string,
  targetDepth: number = 0
): void => {
  if (!editorRef.current?.editor) return;
  const editor = editorRef.current.editor;

  // Find all matching tags in the hierarchy
  const matchingElements: HTMLElement[] = [];
  let currentElement: HTMLElement | null = editor.selection.getNode();

  while (currentElement) {
    if (
      currentElement.getAttribute &&
      currentElement.getAttribute("data-ssml")?.includes(`${tagType}-`)
    ) {
      matchingElements.push(currentElement);
    }
    currentElement = currentElement.parentElement;
  }

  if (matchingElements.length > targetDepth) {
    const ssmlElement = matchingElements[targetDepth];

    // Store the original selection
    const bookmark = editor.selection.getBookmark();

    // Extract the current data-ssml attribute
    const dataSSML = ssmlElement.getAttribute("data-ssml");
    if (!dataSSML) return;

    // Update the tag value
    const dataSSMLValue = `${tagType}-${uid}=${newValue}`;

    // Get the actual content without the styling spans
    const getCleanContent = (element: HTMLElement): string => {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = element.innerHTML;

      const stylingSpans = tempDiv.querySelectorAll(
        ":scope > span[only-styling]"
      );
      stylingSpans.forEach((span) => span.remove());

      // Return the inner HTML with nested tags preserved
      return tempDiv.innerHTML;
    };

    const cleanContent = getCleanContent(ssmlElement);

    // Create new content with the updated tag value
    let newContent = "";
    if (underlineStyleTags.includes(tagType)) {
      newContent = `<span style="border-bottom: 1px solid; display:inline-block; border-bottom-color: ${tagColor}; padding-bottom: 2px;" data-ssml='${dataSSMLValue}'>${cleanContent}</span>`;
    } else if (tagType === SSMLTag.Emphasis) {
      newContent = `<span style="font-weight: 600; font-size: 16px; color: ${tagColor};" data-ssml='${dataSSMLValue}'>${cleanContent}</span>`;
    } else if (tagType === SSMLTag.Phoneme) {
      newContent = `<span data-ssml='${dataSSMLValue}' style="display: inline-block;"><span only-styling style="color: ${tagColor};">{</span>${cleanContent}<span only-styling style="color: ${tagColor};">}</span></span>`;
    } else if (tagType === SSMLTag.Voice) {
      const voiceName = titleCase(newValue.split(", ")[0]);
      newContent = `<span data-ssml='${dataSSMLValue}' style="display: inline-block;"><span only-styling style="color: ${tagColor};">[${voiceName}: </span>${cleanContent}<span only-styling style="color: ${tagColor};">]</span></span>`;
    } else if (tagType === SSMLTag.Style) {
      const styleName = titleCase(newValue.split(", ")[0]);
      newContent = `<span data-ssml='${dataSSMLValue}' style="display: inline-block;"><span only-styling style="color: ${tagColor};">[${styleName}: </span>${cleanContent}<span only-styling style="color: ${tagColor};">]</span></span>`;
    }

    // Replace the node while preserving the parent structure
    (ssmlElement as HTMLElement).outerHTML = newContent;

    editor.selection.moveToBookmark(bookmark);
    editor.fire("change");
  }
};

// Get all tags of a specific type in the hierarchy
export const getAllTagsOfType = (
  editorRef: React.RefObject<EditorRef | null>,
  tagType: SSMLTag
): HTMLElement[] => {
  if (!editorRef.current?.editor) return [];

  const editor = editorRef.current.editor;
  const selectedNode = editor.selection.getNode();
  const matchingElements: HTMLElement[] = [];

  let currentElement: HTMLElement | null = selectedNode;
  while (currentElement) {
    const dataSSML = currentElement.getAttribute("data-ssml");
    if (dataSSML && dataSSML.includes(`${tagType}-`)) {
      matchingElements.push(currentElement);
    }
    currentElement = currentElement.parentElement;
  }

  return matchingElements;
};

// Allow updating a tag at a specific level in nested hierarchy
export const updateSSMLTagAtDepth = (
  editorRef: React.RefObject<EditorRef | null>,
  tagType: SSMLTag,
  newValue: string,
  tagColor: string,
  targetDepth: number = 0
): void => {
  const tags = getAllTagsOfType(editorRef, tagType);

  if (tags.length > targetDepth) {
    const targetTag = tags[targetDepth];
    const dataSSML = targetTag.getAttribute("data-ssml");

    if (dataSSML) {
      // Extract the uid from the data-ssml attribute
      const match = dataSSML.match(new RegExp(`${tagType}-(\\d+)=`));
      const uid = match ? match[1] : Date.now().toString();

      // Use the existing updateSSMLTag function but with the specific element
      updateSSMLTag(editorRef, tagType, newValue, tagColor, uid, targetDepth);
    }
  }
};
